// Constraint drawing, style A: DEVELOPMENT.md, "Appendix: design tokens and drawing specs",
// "Grid and constraints (style A)". Every colour and size a constraint is drawn with is defined
// here, once. Values marked "Appendix" are the spec's own; the others follow its language (pale
// thick lines for line rules, thin grey strokes for arrows and signs, ink for dots and numbers)
// and are listed in docs/DECISIONS.md ("Constraint style A colours").

/** Appendix: Ink. Dots, numbers and outlines. */
export const INK = '#1F1E1C';
/** Appendix: Surface. Circle fills and the patches behind small numbers. */
export const WHITE = '#FFFFFF';
export const FONT = 'Inter, system-ui, sans-serif';

export interface LineStyle {
  color: string;
  width: number;
}

/** Pale lines drawn under the grid lines, through cell centres, with round caps and joins. */
export const LINES = {
  thermo: { color: '#CFCAC1', width: 14 }, // Appendix
  'slow-thermo': { color: '#B3D2DC', width: 14 },
  renban: { color: '#D9B8F2', width: 12 }, // Appendix
  nabner: { color: '#CFE08F', width: 12 },
  /** German whispers (difference 5). */
  whispers: { color: '#8FDB9C', width: 8 }, // Appendix
  'whispers-dutch': { color: '#F7BE84', width: 8 },
  'whispers-custom': { color: '#F4B4C8', width: 8 },
  palindrome: { color: '#BDB8AF', width: 8 },
  'region-sum': { color: '#9CC8EE', width: 8 },
  entropic: { color: '#EBB1E0', width: 8 },
  modular: { color: '#96D5CB', width: 8 },
  parity: { color: '#F2A6A6', width: 8 },
  zipper: { color: '#B0B9F0', width: 8 },
  /** Between and lockout: a pale line whose ends carry the clue (circles or diamonds). */
  between: { color: '#CFCAC1', width: 6 },
  lockout: { color: '#CFCAC1', width: 6 },
  // Pack 8 (2026-11-01): grey as setters draw them, in the palindrome's and thermo's greys (no
  // new colours; FOR_MARKO.md asks Marko to look).
  'kropki-line': { color: '#BDB8AF', width: 8 },
  sequence: { color: '#BDB8AF', width: 8 },
  'bulbless-thermo': { color: '#CFCAC1', width: 14 },
  'pointing-thermo': { color: '#CFCAC1', width: 14 },
  // Pack 9 (2026-11-02): in the palindrome's grey too, a product line with its product as a
  // corner label (FOR_MARKO.md asks Marko to look).
  'product-line': { color: '#BDB8AF', width: 8 },
  'dominant-line': { color: '#BDB8AF', width: 8 },
  // Round 7c (2026-11-02): a liar line in the same grey (FOR_MARKO.md asks Marko to look).
  'liar-line': { color: '#BDB8AF', width: 8 },
} satisfies Record<string, LineStyle>;

/** Thermo and slow-thermo bulbs (Appendix: radius 15). */
export const BULB_RADIUS = 15;
/** Thermos whose tip ends in a cell another thermo uses stop this much short of its centre. */
export const THERMO_TIP_GAP = 9;
/**
 * Between-line circles and lockout diamonds: white, outlined in a darker grey, also when the line
 * has its own colour (several lines may share a circle). Double arrows differ by their thin
 * arrow-grey line.
 */
export const ENDS = { color: '#A8A39A', circle: 15, diamond: 15, strokeWidth: 2 };

/** Arrows, double arrows and their circles (Appendix: #8E8A82, 2 units, circle radius 16). */
export const ARROW = { color: '#8E8A82', width: 2, circle: 16, head: 10, headSpread: 8 };

/** Killer cages (Appendix): dashed, inset 3, sum top-left on a background patch. */
export const CAGE = { color: '#3A3833', width: 1.2, dash: '4 3', inset: 3 };
/** Small numbers in a cell's top-left corner (cage sums, labels). */
export const CORNER_LABEL = { size: 9, weight: 600 };

/** Kropki dots (Appendix: radius 5.5; white with an ink stroke of 1.5, or solid ink). */
export const KROPKI = { radius: 5.5, strokeWidth: 1.5, valueRadius: 7.5, valueSize: 8.5 };
/** XV and other edge sums: ink letters with a white halo. */
export const EDGE_SUM = { size: 13, weight: 700, halo: 3 };
/** Greater-than signs and the minimum/maximum chevrons: thin grey strokes (white halo on edges). */
export const SIGN = { color: '#8E8A82', width: 1.8, arm: 6, halo: 3 };

/** Quadruple circles at a corner: white with an ink border, digits in small text. */
export const QUAD = { radius: 13, strokeWidth: 1.2 };

/** Outside clues: ink numbers one cell outside the grid. */
export const OUTSIDE = { size: 20, weight: 600 };
/** Little killer: a smaller number and a short grey arrow along the diagonal. */
export const LITTLE_KILLER = { size: 14, weight: 600, color: ARROW.color, width: 1.4, head: 4 };

/** Positive and negative diagonals: a thin pale line from corner to corner. */
export const DIAGONAL = { color: '#A9BEDD', width: 2 };

export interface Shade {
  color: string;
  opacity: number;
}

/**
 * Cell shading. Translucent, so the selection and the player's colours still show through.
 * Over white each reads as a pale tint.
 */
export const SHADES = {
  'extra-region': { color: '#7F9CC8', opacity: 0.22 },
  fortress: { color: '#8E8A82', opacity: 0.32 },
  /** Minimum and maximum cells (the chevrons tell them apart). */
  extremum: { color: '#8E8A82', opacity: 0.14 },
  'magic-square': { color: '#C9A961', opacity: 0.2 },
  /** Odd circles and even squares. */
  parity: { color: '#A8A39A', opacity: 0.4 },
} satisfies Record<string, Shade>;

/** Odd: circle radius; even: square side. */
export const ODD_RADIUS = 15;
export const EVEN_SIDE = 30;

/** Clones: each entry (cells and copy) shares one shade, cycling through these. */
export const CLONE_SHADES: readonly Shade[] = [
  { color: '#E8955A', opacity: 0.28 },
  { color: '#5DB872', opacity: 0.28 },
  { color: '#B07FD9', opacity: 0.28 },
  { color: '#D9709A', opacity: 0.26 },
  { color: '#4FB3A8', opacity: 0.28 },
  { color: '#7F9CC8', opacity: 0.3 },
];
